// CAMPOS_POR_SISTEMA: solo para calcular qué claves cuelgan de un valor
// superado (camposSuperadosPorTrayectoria). compilarEscalasInline: la forma
// corta de RASS/SAS para el contexto del "Se recibió" (la MISMA que la prosa
// del sistema usa, cero texto paralelo). Sin ciclo: escalasEvolucion no
// importa de este módulo.
import { CAMPOS_POR_SISTEMA, compilarEscalasInline, dosisConUnidad } from './escalasEvolucion';
import { detectPiiDuro } from './piiDetect';

// La trayectoria del turno (E4 del rediseño, encargo del fundador 30-jul): la
// evolución de la tarde no es una foto, es una película de tres momentos con
// naturaleza distinta.
//
//   | Momento       | Qué es            | Quién lo declara            |
//   |---------------|-------------------|-----------------------------|
//   | "Se recibió"  | HECHO constatado  | La enfermera, en la mañana  |
//   | "Se decidió"  | PLAN, intención   | El equipo, en la visita     |
//   | "Actualmente" | HECHO constatado  | La enfermera, al cierre     |
//
// LA INVARIANTE QUE ESTE ARCHIVO EXISTE PARA PROTEGER, en palabras del
// fundador: "obviamente, corroborado, porque puede pasar que esa sea la idea,
// pero que al final no ocurre". Un PLAN JAMÁS SE PROMUEVE SOLO A HECHO: la
// prosa nunca escribe el estado decidido como estado actual; sin constatación
// dice "pendiente de verificar" y ESO se entrega al turno siguiente. Y cuando
// lo decidido no ocurrió, la brecha se documenta con calma: es el hallazgo
// clínico, no un error de nadie (anti-culpa).
//
// Regla del modelo: NADA SE DEDUCE, TODO SE FIRMA. Los tres momentos son
// declaraciones de una persona, con valor de catálogo cerrado (PII-safe por
// construcción), hora corregible por la autora y ts sellado por el sistema
// (el patrón médico-legal de marcarHoraExamen). La prosa NO se guarda: se
// recompone al imprimir, así corregir un chip corrige el documento.

// ── Ejes: dónde una trayectoria significa algo ────────────────────────────────
// Un eje es un soporte titulable con dirección. Los ESTADOS son observables
// (lo que la enfermera VE), nunca objetivos del equipo: usar el objetivo como
// eje sería pedirle a un plan que se corrobore a sí mismo. Y retirar el
// soporte es un ESTADO de primera clase ("Despierto, sin sedación"): sin eso,
// extubar con éxito se leería como "no verificado" y generaría un pendiente
// falso. El vocabulario calza con los descriptores de RASS ("leve", no
// "ligera").
export const EJES_TRAYECTORIA = [
  {
    id: 'sedacion',
    sistema: 'neuro',
    label: 'La sedación',
    // De menos a más soporte. El ORDEN es semántico: define la dirección.
    estados: [
      { v: 'despierto', label: 'Despierto, sin sedación', prosa: 'despierto, sin sedación' },
      { v: 'leve', label: 'Sedación leve', prosa: 'en sedación leve' },
      { v: 'moderada', label: 'Sedación moderada', prosa: 'en sedación moderada' },
      { v: 'profunda', label: 'Sedación profunda', prosa: 'en sedación profunda' },
    ],
    acciones: [
      { v: 'disminuir', label: 'Disminuir la sedación', inf: 'disminuir la sedación e iniciar despertar' },
      { v: 'suspender', label: 'Suspenderla', inf: 'suspender la sedación' },
      { v: 'profundizar', label: 'Profundizarla', inf: 'profundizar la sedación' },
      { v: 'mantener', label: 'Mantenerla', inf: 'mantener la sedación' },
    ],
    pendienteVerificar: 'Verificar el descenso de sedación',
    pendienteBrecha: 'Descenso de sedación indicado, aún no logrado',
  },
  {
    // Nació del retiro de la sección ATB de la visita técnica (30-jul): la
    // decisión de la visita (iniciar/suspender/mantener el ATB) es material
    // "se decidió" de manual: suspender indicado y no ejecutado es
    // exactamente la brecha que se entrega. El detalle del esquema vive en
    // infecto; acá solo la dirección observable.
    id: 'atb',
    sistema: 'infecto',
    label: 'La terapia antibiótica',
    estados: [
      { v: 'sin', label: 'Sin ATB', prosa: 'sin terapia antibiótica' },
      { v: 'en_curso', label: 'ATB en curso', prosa: 'con terapia antibiótica en curso' },
    ],
    acciones: [
      { v: 'suspender', label: 'Suspenderla', inf: 'suspender la terapia antibiótica' },
      { v: 'profundizar', label: 'Iniciarla', inf: 'iniciar terapia antibiótica' },
      { v: 'mantener', label: 'Mantenerla', inf: 'mantener la terapia antibiótica' },
    ],
    pendienteVerificar: 'Verificar la decisión de ATB de la visita',
    pendienteBrecha: 'Decisión de ATB indicada, aún no ejecutada',
  },
];

export function ejeDe(id) {
  return EJES_TRAYECTORIA.find((e) => e.id === id) || null;
}

// ── Registro de los momentos ──────────────────────────────────────────────────
// `hora` la corrige la autora (la visita pasó a las 12:30 y la documentó a las
// 13:10: la hora clínica es 12:30). `ts` lo sella el sistema al PRIMER registro
// del momento y no se edita jamás: es la evidencia de cuándo se escribió, el
// mismo contrato de marcarHoraExamen.
let _seq = 0;
export function nuevaTrayectoria(eje) {
  _seq += 1;
  return { id: `tr-${_seq}-${Math.random().toString(36).slice(2, 7)}`, eje };
}

function momento(previo, valorCampo, valor, hora) {
  return {
    ...(previo || {}),
    [valorCampo]: valor,
    hora: hora || previo?.hora || null,
    ts: previo?.ts || new Date().toISOString(),
  };
}

export function registrarRecibido(t, estado, hora = null) {
  return { ...t, recibido: momento(t.recibido, 'estado', estado, hora) };
}
export function registrarDecision(t, accion, hora = null) {
  return { ...t, decision: momento(t.decision, 'accion', accion, hora) };
}
export function registrarConstatado(t, estado, hora = null) {
  return { ...t, constatado: momento(t.constatado, 'estado', estado, hora) };
}

// ── El veredicto de la dirección ─────────────────────────────────────────────
// ¿Lo constatado va en la dirección de lo decidido? Devuelve:
//   'cumplido' | 'parcial' | 'brecha' | null (sin decisión o sin constatación).
// La brecha NO es un error: es el hallazgo que se entrega.
export function veredictoTrayectoria(t, eje = null) {
  const def = eje || ejeDe(t?.eje);
  if (!def || !t?.decision?.accion || !t?.constatado?.estado || !t?.recibido?.estado) return null;
  const idx = (v) => def.estados.findIndex((e) => e.v === v);
  const iRec = idx(t.recibido.estado);
  const iCon = idx(t.constatado.estado);
  if (iRec < 0 || iCon < 0) return null;
  const a = t.decision.accion;
  if (a === 'mantener') return iCon === iRec ? 'cumplido' : 'brecha';
  if (a === 'suspender') {
    if (iCon === 0) return 'cumplido';
    return iCon < iRec ? 'parcial' : 'brecha';
  }
  if (a === 'disminuir') {
    // Ya recibido en el mínimo del eje: no hay hacia dónde bajar. Constatar el
    // mismo mínimo es cumplido (no una brecha absurda); subir sí es brecha.
    if (iRec === 0) return iCon === 0 ? 'cumplido' : 'brecha';
    if (iCon < iRec) return iCon === 0 ? 'cumplido' : (iRec - iCon >= 2 || iCon === 1 ? 'cumplido' : 'parcial');
    return 'brecha';
  }
  if (a === 'profundizar') {
    // Espejo: recibido en el máximo, mantenerse ahí es cumplido.
    if (iRec === def.estados.length - 1) return iCon === iRec ? 'cumplido' : 'brecha';
    return iCon > iRec ? 'cumplido' : 'brecha';
  }
  return null;
}

// ── La prosa, recompuesta al imprimir (jamás guardada) ───────────────────────
// Tiempos verbales por momento: "Se recibió" (hecho pasado), "se decide"
// (plan del día), "se constata" / "se mantiene" (hecho actual). LA GUARDA
// VIVE ACÁ: la única frase posible para una decisión sin constatar es
// "pendiente de verificar al cierre del turno". No existe camino por el que
// el estado decidido se escriba como estado actual.

// Los marcadores léxicos de la película, en UNA sola constante: `lineaTrayectoria`
// los usa para ESCRIBIR y la red del pulido (`verificarTrayectoria` en
// evolucionIA.js) para RECONOCER esas líneas y exigirlas literales. Fuente
// única a propósito: una tabla paralela en la red se desincronizaría del día
// que alguien cambie un tiempo verbal acá, y la guarda quedaría mirando frases
// que ya nadie imprime.
export const MARCADORES_TRAYECTORIA = {
  recibido: 'Se recibió ',
  decision: 'Durante el día se decide ',
  cierre: 'Al cierre ',
  hora: 'A las ',
  constata: 'se constata ',
  mantiene: 'se mantiene ',
  // La suspensión dictada de un fármaco de sedoanalgesia (2026-09-08, caso L):
  // "A las 11:00 se suspende propofol." es película, no constatación de un
  // estado, y el sándwich del pulido la protege igual que a las demás.
  suspende: 'se suspende ',
  // La película SIN cambio se dice en UNA frase de presente (dictado del
  // fundador en su turno real, 2026-08-18: "Se recibió con terapia
  // antibiótica en curso. Durante el día se decide mantenerla. A las 14:38 se
  // constata con terapia antibiótica en curso" son tres frases para decir
  // NADA — "mejor decir se encuentra con tto antibiótico en curso").
  encuentra: 'Se encuentra ',
  pendiente: 'Pendiente de verificar al cierre del turno.',
  brecha: 'aún no se logra',
  entrega: 'Se entrega pendiente al turno siguiente.',
};

// Reconocedor compartido por el pulido y la presentación. Los marcadores se
// anclan al inicio de la línea y conservan su espacio final: `\b` no reconoce
// el borde tras la vocal acentuada de "Se recibió ".
export const RE_LINEA_TRAYECTORIA = (() => {
  const M = MARCADORES_TRAYECTORIA;
  const e = (t) => String(t).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const cuando = `(?:${e(M.cierre)}|${e(M.hora)}\\d{1,2}:\\d{2}\\s)(?:${e(M.constata)}|${e(M.mantiene)}|${e(M.suspende)})`;
  // M.encuentra: la película sin cambio colapsada a una frase de presente
  // (dictado 2026-08-18) también es línea de trayectoria y el pulido no
  // puede perderla. M.hora se conserva en el reconocedor por tolerancia a
  // borradores viejos aunque la prosa nueva ya no lo imprime.
  return new RegExp(`^(?:${e(M.recibido)}|${e(M.decision)}|${e(M.encuentra)}|${cuando})`, 'i');
})();

// `contexto` (opcional, 2026-08-18): el detalle de la foto superada, anclado
// entre paréntesis a la frase del "Se recibió" — ver contextoRecibidoTrayectoria.
export function lineaTrayectoria(t, { contexto = null } = {}) {
  const def = ejeDe(t?.eje);
  if (!def) return '';
  const prosaDe = (v) => def.estados.find((e) => e.v === v)?.prosa || null;
  const partes = [];

  // Las horas viven en los DATOS, no en la prosa (norma del fundador
  // 2026-08-02, re-dictada en su turno real del 2026-08-18: "sin horas no es
  // necesario, a no ser que sea algo puntual" — y constatar un ESTADO no es
  // algo puntual). "Se recibió", "durante el día" y "al cierre" ya ubican en
  // el tiempo; la hora y el ts siguen sellados en el registro.
  const M = MARCADORES_TRAYECTORIA;
  const rec = prosaDe(t.recibido?.estado);
  const acc = def.acciones.find((a) => a.v === t.decision?.accion);
  const con = prosaDe(t.constatado?.estado);
  const veredicto = con ? veredictoTrayectoria(t, def) : null;

  // Película SIN cambio (dictado 2026-08-18): recibido y constatado son el
  // mismo estado, y lo decidido fue mantener (o no hubo decisión). Contar los
  // tres momentos era plantilla pura; queda UNA frase de presente. Cualquier
  // decisión de cambio conserva la película completa: ahí los tres momentos
  // SÍ son la historia (incluida la brecha).
  const sinCambio = rec && con && t.recibido.estado === t.constatado.estado
    && (!t.decision?.accion || (t.decision.accion === 'mantener' && veredicto === 'cumplido'));
  if (sinCambio) return `${M.encuentra}${con}.`;

  if (rec) partes.push(`${M.recibido}${rec}${contexto ? ` (${contexto})` : ''}.`);
  if (acc) partes.push(`${M.decision}${acc.inf}.`);

  if (con) {
    if (veredicto === 'brecha') {
      // La brecha se dice con calma y completa: estado real + lo indicado que
      // aún no se logra. Es el hallazgo, no un reproche.
      partes.push(`${M.cierre}${M.mantiene}${con}: lo indicado (${acc ? acc.inf : 'el cambio'}) ${M.brecha}. ${M.entrega}`);
    } else if (veredicto === 'parcial') {
      partes.push(`${M.cierre}${M.constata}${con}, cambio en curso.`);
    } else {
      partes.push(`${M.cierre}${M.constata}${con}.`);
    }
  } else if (acc) {
    // LA GUARDA: decisión sin constatación NUNCA se da por ocurrida.
    partes.push(M.pendiente);
  }

  return partes.join(' ');
}

// ── Re-conciliación foto ↔ película ──────────────────────────────────────────
// ¿La trayectoria constatada CONTRADICE lo que los campos del sistema aún
// dicen? El hallazgo del panel ciego, cobrado en el turno real del 18-ago: la
// evolución decía "despierto, sin sedación" y tres líneas después "Sedado,
// RASS -2, con propofol", sembrado en la mañana y nunca actualizado. No se
// retracta ningún campo (los puentes SOLO AGREGAN, regla fijada dos veces):
// se AVISA para que la autora reconcilie — nada muere mudo. Catálogo cerrado:
// avisos fijos por eje, jamás texto del registro.
export function conflictosTrayectoria(trayectorias = [], evolucion = null) {
  const out = [];
  const campos = (sis) => evolucion?.sistemas?.[sis]?.campos || {};
  for (const t of Array.isArray(trayectorias) ? trayectorias : []) {
    if (t?.eje === 'sedacion' && t?.constatado?.estado === 'despierto'
        && campos('neuro').sedacion === 'Con sedación') {
      out.push({
        eje: 'sedacion',
        sistema: 'neuro',
        aviso: 'Constataste despierto sin sedación, pero tu evolución de neuro aún dice "Con sedación". Revísala para que el documento no se contradiga.',
      });
    }
    // El espejo (sedación iniciada en la tarde, campos de la mañana aún en
    // "Sin sedación") es el mismo choque en la otra dirección.
    if (t?.eje === 'sedacion' && ['leve', 'moderada', 'profunda'].includes(t?.constatado?.estado)
        && campos('neuro').sedacion === 'Sin sedación') {
      out.push({
        eje: 'sedacion',
        sistema: 'neuro',
        aviso: 'Constataste sedación, pero tu evolución de neuro aún dice "Sin sedación". Revísala para que el documento no se contradiga.',
      });
    }
    if (t?.eje === 'atb' && t?.constatado?.estado === 'sin'
        && String(campos('infecto').atbEsquema || '').trim()) {
      out.push({
        eje: 'atb',
        sistema: 'infecto',
        aviso: 'Constataste sin terapia antibiótica, pero tu evolución de infecto aún trae un esquema. Revísala para que el documento no se contradiga.',
      });
    }
  }
  return out;
}

// ── La película MANDA sobre la foto en la prosa ──────────────────────────────
// Orden directa del fundador (2026-08-18, en su turno: "la idea de las notas
// es que de eso salga una evolución"): el documento no puede afirmar como
// PRESENTE lo que la propia trayectoria constató que cambió — su neuro decía
// "despierto, sin sedación" y tres líneas después "Sedado, RASS -2, con
// propofol", el campo sembrado en la mañana narrado como ahora.
//
// El mecanismo es la doctrina ya ganada en la ola Bupa, "omisión con portador
// vivo": el CAMPO no se toca (los puentes solo agregan; la Mesa lo sigue
// mostrando y el aviso de conflictosTrayectoria pide reconciliarlo) — lo que
// se calla es su NARRACIÓN, y solo mientras el portador (la línea de la
// trayectoria de ese sistema) viva en el documento. buildEvolucion exige esa
// condición: sin línea de trayectoria, la narración VUELVE. Fail-safe hacia
// narrar: solo se apagan las claves cuya dependencia cuelga COMPLETA del valor
// superado (una dependencia OR con otra vía viva, o un noEn, se sigue
// narrando aunque quede huérfana — narrar de más es un defecto de estilo,
// callar de más borra un hecho).
const ESCALAS_SEDACION = ['rass', 'sas'];

// Exportada desde la película de la cronología (2026-08-22): el retiro de un
// dispositivo supera su campo sembrado con el MISMO cierre transitivo que la
// trayectoria de la card — una tabla propia en el bridge se desincronizaría.
export function clavesQueColganDe(sistemaId, raizK, campos) {
  const defs = CAMPOS_POR_SISTEMA[sistemaId] || [];
  const out = new Set([raizK]);
  let crecio = true;
  while (crecio) {
    crecio = false;
    for (const c of defs) {
      if (out.has(c.k) || !c.dependeDe) continue;
      const deps = Array.isArray(c.dependeDe) ? c.dependeDe : [c.dependeDe];
      const cuelgaEntera = deps.every((d) => d && out.has(d.campo)
        && (d.en ? d.en.includes(campos?.[d.campo]) : !d.noEn));
      if (cuelgaEntera) { out.add(c.k); crecio = true; }
    }
  }
  return [...out];
}

// ── Relacionar, no solo callar (dictado del fundador, 2026-08-18) ────────────
// "Mi idea no es que simplemente se calle, es que se relacionen, tomen
// contexto": el dato superado describe el momento en que SÍ era cierto, la
// recepción, así que en vez de desaparecer del documento se MUDA a la frase
// del "Se recibió" como su detalle:
//
//   "Se recibió en sedación moderada (propofol 30 ml/h, RASS -2). Durante el
//    día se decide suspender la sedación. Al cierre se constata despierto,
//    sin sedación."
//
// Deriva de los MISMOS conflictos del aviso (fuente única: sin conflicto no
// hay nada que mudar) y SOLO cuando lo recibido es coherente con el valor
// viejo (recibido sedado con campos "Con sedación"; recibido con ATB y
// esquema vivo). Si lo recibido TAMBIÉN contradice la foto vieja (se recibió
// despierto y los campos dicen sedado), no existe momento al que anclarla:
// queda la omisión con su aviso, que es lo honesto. Solo catálogo + números +
// el esquema que el documento ya imprime, cada texto libre tras la guarda
// dura de PII; las horas siguen en los datos.
function listaProsa(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

export function contextoRecibidoTrayectoria(t, evolucion = null) {
  const def = ejeDe(t?.eje);
  if (!def || !evolucion) return null;
  if (!conflictosTrayectoria([t], evolucion).some((c) => c.eje === t.eje)) return null;
  const sis = evolucion.sistemas?.[def.sistema] || {};
  const campos = sis.campos || {};
  const partes = [];
  if (t.eje === 'sedacion' && ['leve', 'moderada', 'profunda'].includes(t.recibido?.estado)) {
    // La droga se re-valida contra las opciones del catálogo (regla #642/#721:
    // jsonb adulterado jamás imprime) — la Mesa la muestra para corregirla,
    // pero a esta línea solo entra vocabulario propio.
    const catalogoSedo = (CAMPOS_POR_SISTEMA.neuro || []).find((c) => c.k === 'sedoanalgesia')?.opciones || [];
    const drogas = (Array.isArray(campos.sedoanalgesia) ? campos.sedoanalgesia : [])
      .filter((d) => catalogoSedo.includes(d))
      .map((d) => {
        // La dosis es texto de la autora: solo viaja limpia de PII dura.
        const dosis = dosisConUnidad(d, campos.sedoDetalle?.[d]?.dosis);
        return dosis && !detectPiiDuro(dosis).hit ? `${d.toLowerCase()} ${dosis}` : d.toLowerCase();
      });
    if (drogas.length) partes.push(`con ${listaProsa(drogas)}`);
    // RASS/SAS en su forma corta canónica (compilarEscalasInline): son las
    // mismas escalas que camposSuperadosPorTrayectoria calla en el bloque.
    for (const e of compilarEscalasInline('neuro', sis.escalas, campos)) {
      if (/^(RASS|SAS)\b/.test(e)) partes.push(e);
    }
    // BIS re-validado al leer: jsonb adulterado jamás imprime.
    const bis = String(campos.bis ?? '').trim();
    if (/^\d{1,3}$/.test(bis)) partes.push(`BIS ${bis}`);
  }
  if (t.eje === 'atb' && t.recibido?.estado === 'en_curso') {
    const esquema = String(campos.atbEsquema || '').trim();
    if (esquema && !detectPiiDuro(esquema).hit) partes.push(esquema);
    const dia = diaAtbPortable(t, campos);
    if (dia) partes.push(`día ${dia} de tratamiento`);
  }
  return partes.length ? partes.join(', ') : null;
}

// ¿El contexto del "Se recibido" PORTA el día de ATB? La MISMA condición
// decide tejerlo (contextoRecibidoTrayectoria) y callarlo como presente
// (camposSuperadosPorTrayectoria): atbDia no cuelga de atbEsquema en el
// catálogo, así que la omisión general no lo alcanza, y sin este espejo el
// documento lo decía DOS veces — dentro del paréntesis y de nuevo como
// presente justo después de constatar sin ATB (hallazgo bloqueante de la
// auditoría del 18-ago). Sin portador (recibido no en_curso, o día
// inválido), el día se sigue narrando: callar sin portador amputa el dato.
function diaAtbPortable(t, campos) {
  if (t?.recibido?.estado !== 'en_curso') return null;
  const dia = String(campos?.atbDia ?? '').trim();
  return /^\d{1,3}$/.test(dia) ? dia : null;
}

// → { [sistemaId]: { campos: [k…], escalas: [id…] } }, derivado de los MISMOS
// conflictos del aviso (fuente única: si no hay conflicto, no se calla nada).
export function camposSuperadosPorTrayectoria(trayectorias = [], evolucion = null) {
  const out = {};
  const agregar = (sis, campos, escalas) => {
    const o = out[sis] || (out[sis] = { campos: [], escalas: [] });
    for (const k of campos) if (!o.campos.includes(k)) o.campos.push(k);
    for (const e of escalas) if (!o.escalas.includes(e)) o.escalas.push(e);
  };
  for (const t of Array.isArray(trayectorias) ? trayectorias : []) {
    for (const c of conflictosTrayectoria([t], evolucion)) {
      const campos = evolucion?.sistemas?.[c.sistema]?.campos || {};
      if (c.eje === 'sedacion') {
        // Las escalas de sedación (RASS/SAS) son parte de la misma foto vieja:
        // afirmar "RASS -2" como ahora en un despierto constatado es el mismo
        // choque. Sus valores siguen en los datos.
        agregar('neuro', clavesQueColganDe('neuro', 'sedacion', campos), ESCALAS_SEDACION);
      }
      if (c.eje === 'atb') {
        const claves = clavesQueColganDe('infecto', 'atbEsquema', campos);
        // El día de ATB se calla SOLO cuando el contexto del "Se recibió" lo
        // porta (diaAtbPortable, la misma condición que lo teje): sin este
        // espejo el documento lo decía dos veces, y callarlo sin portador
        // amputaría el dato.
        if (diaAtbPortable(t, campos)) claves.push('atbDia');
        agregar('infecto', claves, []);
      }
    }
  }
  return out;
}

// ── Lo que la entrega hereda ──────────────────────────────────────────────────
// Una decisión sin constatar, o constatada con brecha, ES un pendiente de la
// R del SBAR: se entrega, no se calla y no acusa.
export function pendienteTrayectoria(t) {
  const def = ejeDe(t?.eje);
  if (!def || !t?.decision?.accion) return null;
  if (!t?.constatado?.estado) return def.pendienteVerificar;
  return veredictoTrayectoria(t, def) === 'brecha' ? def.pendienteBrecha : null;
}

// Todas las líneas por sistema, para inyectar en buildEvolucion.
// → { [sistemaId]: [linea, ...] }
// Con `evolucion`, la foto superada viaja como contexto del "Se recibió"
// (contextoRecibidoTrayectoria); sin ella, el output es el de siempre.
export function lineasTrayectoriaPorSistema(trayectorias = [], evolucion = null) {
  const por = {};
  for (const t of Array.isArray(trayectorias) ? trayectorias : []) {
    const def = ejeDe(t?.eje);
    if (!def) continue;
    const linea = lineaTrayectoria(t, { contexto: contextoRecibidoTrayectoria(t, evolucion) });
    if (!linea) continue;
    (por[def.sistema] = por[def.sistema] || []).push(linea);
  }
  return por;
}
